import { setupOfflineInterceptors } from "@/domains/offline/api/setupOfflineInterceptors";
import { setupCsrfInterceptor } from "@/domains/shared/services/setupCsrfInterceptor";
import { setupDeployVersionInterceptor } from "@/domains/shared/services/setupDeployVersionInterceptor";
import { setupErrorEnvelopeInterceptor } from "@/domains/shared/services/setupErrorEnvelopeInterceptor";
import { setupFormDataMultipartInterceptor } from "@/domains/shared/services/setupFormDataMultipartInterceptor";
import { setupSessionInterceptor } from "@/domains/shared/services/setupSessionInterceptor";
import { setupTokenRefreshInterceptor } from "@/domains/shared/services/setupTokenRefreshInterceptor";
import { getTranslation, type MivoTranslationType } from "@mivo/i18n";
import axios, { AxiosError } from "axios";

import { API_MESSAGE_TRANSLATIONS } from "@/domains/shared/services/apiMessageTranslations";
import { ApiMessageCodes } from "@/domains/shared/services/apiMessageCodes";

export const API_ERROR_TRANSLATIONS = {
  network: {
    "pt-BR": "Não foi possível conectar ao servidor. Verifique sua internet.",
    en: "Could not connect to the server. Check your internet connection.",
    es: "No se pudo conectar al servidor. Verifica tu conexión a internet.",
    de: "Es konnte keine Verbindung zum Server hergestellt werden. Überprüfen Sie Ihre Internetverbindung.",
  },
  unauthorized: {
    "pt-BR": "Sessão expirada ou inválida. Faça login novamente.",
    en: "Session expired or invalid. Sign in again.",
    es: "Sesión expirada o inválida. Inicia sesión de nuevo.",
    de: "Sitzung abgelaufen oder ungültig. Melden Sie sich erneut an.",
  },
  forbidden: {
    "pt-BR": "Você não tem permissão para esta ação.",
    en: "You do not have permission for this action.",
    es: "No tienes permiso para esta acción.",
    de: "Sie haben keine Berechtigung für diese Aktion.",
  },
  badRequest: {
    "pt-BR": "Requisição inválida. Revise os dados e tente novamente.",
    en: "Invalid request. Review the data and try again.",
    es: "Solicitud inválida. Revisa los datos e inténtalo de nuevo.",
    de: "Ungültige Anfrage. Überprüfen Sie die Daten und versuchen Sie es erneut.",
  },
  server: {
    "pt-BR": "Erro no servidor. Tente novamente em instantes.",
    en: "Server error. Try again shortly.",
    es: "Error del servidor. Inténtalo de nuevo en unos momentos.",
    de: "Serverfehler. Versuchen Sie es in Kürze noch einmal.",
  },
  generic: {
    "pt-BR": "Não foi possível concluir a operação. Tente novamente.",
    en: "Could not complete the operation. Try again.",
    es: "No se pudo completar la operación. Inténtalo de nuevo.",
    de: "Der Vorgang konnte nicht abgeschlossen werden. Versuchen Sie es erneut.",
  },
} as const;

const backendOrigin = import.meta.env.VITE_BACKEND_URL?.replace(/\/$/, "") ?? "";

/** Base URL for JSON API calls (`/api` when unset — use Vite proxy in dev or same-origin deploy). */
export const API = backendOrigin ? `${backendOrigin}/api` : "/api";

/**
 * Absolute URL for full-page API navigation (OAuth, calendar connect).
 * Must match `API` origin — relative `/api/...` alone uses the SPA host and can 404 when API is elsewhere.
 */
export function buildApiBrowserUrl(path: string): string {
    const normalized = path.startsWith("/") ? path : `/${path}`;
    const apiPath = normalized.startsWith("/api/") ? normalized : `/api${normalized}`;
    return backendOrigin ? `${backendOrigin}${apiPath}` : apiPath;
}

/** SignalR chat hub (`/hubs/chat` when unset — Vite proxies `/hubs` in dev). */
export const CHAT_HUB_URL = backendOrigin ? `${backendOrigin}/hubs/chat` : "/hubs/chat";

/** SignalR session agenda hub (`/hubs/sessionAgenda` when unset — Vite proxies `/hubs` in dev). */
export const SESSION_AGENDA_HUB_URL = backendOrigin
  ? `${backendOrigin}/hubs/sessionAgenda`
  : "/hubs/sessionAgenda";

/**
 * When `VITE_BACKEND_URL` is set, the SPA talks to another origin and cookies may not reach SignalR negotiate:
 * use GET `/auth/realtime-token` + query JWT. When unset (same-origin `/api` + `/hubs` via proxy), cookies are enough.
 */
export const SIGNALR_ACCESS_TOKEN_QUERY = Boolean(backendOrigin);

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  timeout: 30_000,
});

setupOfflineInterceptors(api);
setupCsrfInterceptor(api);
setupDeployVersionInterceptor(api);
setupErrorEnvelopeInterceptor(api);
setupSessionInterceptor(api);
setupTokenRefreshInterceptor(api);
setupFormDataMultipartInterceptor(api);

export async function getSignalRAccessToken(): Promise<string> {
  if (!SIGNALR_ACCESS_TOKEN_QUERY) {
    return "";
  }

  try {
    const { data } = await api.get<{ accessToken?: string }>("/auth/realtime-token");
    return data.accessToken ?? "";
  } catch {
    return "";
  }
}

export type ApiValidationDetail =
  | string
  | { msg?: string }
  | Array<{ msg?: string } | string>;

const KNOWN_API_ERROR_MESSAGES: Record<string, string> = {
  "No access to this patient.": "Sem permissão para alterar este paciente (vínculo encerrado ou sem acesso).",
  "Incomplete profile: add your professional council number.":
    "Complete o número do conselho no perfil para continuar.",
  "Forbidden.": "Você não tem permissão para esta ação.",
  "Not authenticated.": "Não autenticado.",
};

/** Safe interpolation values from API `params` (BR-06). */
export type ApiMessageParams = Record<string, string | number | boolean | null | undefined>;

export type FormatApiErrorOptions = {
  code?: string | null;
  params?: ApiMessageParams | null;
};

/** Replaces `{key}` placeholders with values from `params` when present. */
export function applyApiMessageParams(template: string, params?: ApiMessageParams | null): string {
  if (params == null) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = params[key];
    return value == null ? match : String(value);
  });
}

/**
 * Resolves user-facing API error text (BR-15 / FR-04):
 * 1. `code` → API message translation map + optional params
 * 2. Legacy known-`detail` English remap
 * 3. Raw `detail` (or empty)
 */
export function formatApiError(detail: unknown, options?: FormatApiErrorOptions): string {
  const code = typeof options?.code === "string" ? options.code.trim() : "";
  if (code) {
    const entry = (API_MESSAGE_TRANSLATIONS as Record<string, MivoTranslationType | undefined>)[code];
    if (entry) {
      return applyApiMessageParams(getTranslation(entry), options?.params);
    }
  }

  if (detail == null) {
return "";
}
  if (typeof detail === "string") {
    return KNOWN_API_ERROR_MESSAGES[detail] ?? detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((e) =>
        e && typeof e === "object" && "msg" in e && typeof (e as { msg?: string }).msg === "string"
          ? (e as { msg: string }).msg
          : JSON.stringify(e),
      )
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail === "object" && "msg" in detail && typeof (detail as { msg: string }).msg === "string") {
    return (detail as { msg: string }).msg;
  }
  return String(detail);
}

function readApiMessageParams(value: unknown): ApiMessageParams | null {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as ApiMessageParams;
}

function formatFromApiErrorRecord(record: {
  code?: unknown;
  detail?: unknown;
  title?: unknown;
  params?: unknown;
}): string {
  const code = typeof record.code === "string" ? record.code : null;
  const params = readApiMessageParams(record.params);
  return formatApiError(record.detail ?? record.title, { code, params });
}

/** Reads ASP.NET `{ detail }` / ProblemDetails JSON from a blob error body (`responseType: "blob"`). */
function isBlobData(data: unknown): data is Blob {
  if (data == null || typeof data !== "object") {
    return false;
  }
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return true;
  }
  const candidate = data as Blob;
  return typeof candidate.arrayBuffer === "function" || typeof candidate.text === "function";
}

function readBlobViaFileReader(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsText(blob);
  });
}

async function readBlobText(blob: Blob): Promise<string> {
  const decodeBuffer = (buffer: ArrayBuffer) => new TextDecoder().decode(buffer);

  if (typeof blob.arrayBuffer === "function") {
    try {
      const fromBuffer = decodeBuffer(await blob.arrayBuffer());
      if (fromBuffer.length > 0) {
        return fromBuffer;
      }
    } catch {
      /* fall through */
    }
  }

  if (typeof blob.text === "function") {
    try {
      const fromText = await blob.text();
      if (fromText.length > 0) {
        return fromText;
      }
    } catch {
      /* fall through */
    }
  }

  if (typeof FileReader !== "undefined") {
    try {
      return await readBlobViaFileReader(blob);
    } catch {
      return "";
    }
  }

  return "";
}

/** Decodes API JSON errors from blob bodies, strings, buffers, or plain JSON objects. */
export async function parseApiErrorBody(data: unknown): Promise<string> {
  if (data == null) {
    return "";
  }

  if (typeof data === "object" && !(data instanceof Blob) && !ArrayBuffer.isView(data) && !(data instanceof ArrayBuffer)) {
    if ("detail" in data || "title" in data || "code" in data) {
      return formatFromApiErrorRecord(data as {
        code?: unknown;
        detail?: unknown;
        title?: unknown;
        params?: unknown;
      });
    }
  }

  let text = "";
  try {
    if (typeof data === "string") {
      text = data;
    } else if (data instanceof ArrayBuffer) {
      text = new TextDecoder().decode(data);
    } else if (ArrayBuffer.isView(data)) {
      const view = data as ArrayBufferView;
      text = new TextDecoder().decode(
        view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength),
      );
    } else if (isBlobData(data)) {
      text = await readBlobText(data);
    }
  } catch {
    return "";
  }

  if (!text.trim()) {
    return "";
  }

  try {
    const parsed = JSON.parse(text) as {
      code?: unknown;
      detail?: unknown;
      title?: unknown;
      params?: unknown;
    };
    return formatFromApiErrorRecord(parsed);
  } catch {
    return "";
  }
}

/** @deprecated Use {@link parseApiErrorBody}. */
export async function parseApiErrorBlob(blob: Blob): Promise<string> {
  return parseApiErrorBody(blob);
}

/** Nginx/Cloudflare HTML error pages must not surface as API `detail` in toasts. */
function isGatewayHtmlBody(value: string): boolean {
  const trimmed = value.trimStart().toLowerCase();
  return trimmed.startsWith("<!doctype html") || trimmed.startsWith("<html");
}

export function getAxiosDetail(err: unknown): unknown {
  const ax = err as AxiosError<{
    detail?: ApiValidationDetail;
    title?: string;
    code?: string;
    params?: ApiMessageParams;
  }>;
  const data = ax.response?.data;
  if (isBlobData(data)) {
    return undefined;
  }
  if (data != null && typeof data === "object" && "detail" in data) {
    return data.detail;
  }
  if (typeof data === "string" && (data as string).trim().length > 0) {
    if (isGatewayHtmlBody(data)) {
      return undefined;
    }
    return data;
  }
  if (data != null && typeof data === "object" && "title" in data && typeof data.title === "string") {
    return data.title;
  }
  return undefined;
}

function getAxiosMessageFields(err: unknown): FormatApiErrorOptions & { detail: unknown } {
  const ax = err as AxiosError<{
    detail?: ApiValidationDetail;
    title?: string;
    code?: string;
    params?: ApiMessageParams;
  }>;
  const data = ax.response?.data;
  if (isBlobData(data) || data == null || typeof data !== "object") {
    return { detail: getAxiosDetail(err) };
  }
  const record = data as {
    code?: unknown;
    detail?: unknown;
    title?: unknown;
    params?: unknown;
  };
  return {
    detail: "detail" in record ? record.detail : "title" in record ? record.title : undefined,
    code: typeof record.code === "string" ? record.code : null,
    params: readApiMessageParams(record.params),
  };
}

/** User-facing message from any API/axios failure (includes status fallbacks when body has no `detail`). */
export function describeApiError(err: unknown): string {
  if (err instanceof AxiosError || axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") {
      return getTranslation(API_ERROR_TRANSLATIONS.network);
    }

    const fields = getAxiosMessageFields(err);
    const fromDetail = formatApiError(fields.detail, {
      code: fields.code,
      params: fields.params,
    });
    if (fromDetail) {
      return fromDetail;
    }

    const status = err.response?.status;
    if (status === 401) {
      return getTranslation(API_ERROR_TRANSLATIONS.unauthorized);
    }
    if (status === 403) {
      return getTranslation(API_ERROR_TRANSLATIONS.forbidden);
    }
    if (status === 400) {
      return getTranslation(API_ERROR_TRANSLATIONS.badRequest);
    }
    if (status === 409) {
      return getTranslation(API_MESSAGE_TRANSLATIONS[ApiMessageCodes.conflict]);
    }
    if (status != null && status >= 500) {
      return getTranslation(API_ERROR_TRANSLATIONS.server);
    }
  }

  if (err instanceof Error && err.message.trim().length > 0) {
    return err.message;
  }

  return getTranslation(API_ERROR_TRANSLATIONS.generic);
}

/** Like {@link describeApiError}, but also decodes `response.data` when it is a `Blob`. */
export async function describeApiErrorAsync(err: unknown): Promise<string> {
  const responseData = (err as { response?: { data?: unknown } })?.response?.data;
  const fromBody = await parseApiErrorBody(responseData);
  if (fromBody) {
    return fromBody;
  }

  return describeApiError(err);
}

export function getResponseHeader(headers: unknown, name: string): string | undefined {
  if (headers == null) {
    return undefined;
  }

  const normalized = name.toLowerCase();
  if (typeof headers === "object" && "get" in headers && typeof headers.get === "function") {
    const value = (headers as { get: (key: string) => unknown }).get(normalized);
    return value != null ? String(value) : undefined;
  }

  const record = headers as Record<string, string | string[] | undefined>;
  const direct = record[normalized] ?? record[name];
  if (Array.isArray(direct)) {
    return direct[0];
  }

  return direct;
}
